import type { LeagueNextTarget } from "@core/league/types";

export type LeagueTargetTone = "go" | "danger" | "calm";

export type LeagueNextTargetText = {
  /** Translation key; `:points` is a phrase ("8 نقط"), `:name` a student. */
  key: string;
  params: { points: number; name: string };
  tone: LeagueTargetTone;
};

/**
 * Sentence descriptor for a next target (§3.8 of the spec). Unknown kinds
 * return null so the card is simply hidden. A target that needs a student
 * but has none falls back to the nameless sentence.
 */
export function nextTargetText(
  target: LeagueNextTarget | null | undefined,
): LeagueNextTargetText | null {
  if (!target || !target.kind) return null;

  const name = target.student?.name?.trim() ?? "";
  const params = { points: target.points, name };
  const withName = (base: string, tone: LeagueTargetTone) => ({
    key: name ? `league.target.${base}` : `league.target.${base}NoName`,
    params,
    tone,
  });

  switch (target.kind) {
    case "start":
      return { key: "league.target.start", params, tone: "go" };
    case "alone":
      return { key: "league.target.alone", params, tone: "calm" };
    case "escapeDrop":
      return withName("escapeDrop", "danger");
    case "reachPromotion":
      return { key: "league.target.reachPromotion", params, tone: "go" };
    case "reachPodium":
      return { key: "league.target.reachPodium", params, tone: "go" };
    case "holdLead":
      return withName("holdLead", "calm");
    case "passNext":
      return withName("passNext", "go");
    default:
      return null;
  }
}

/** Arabic / English plural bucket used by `league.units.*` and `league.pointsWord.*`. */
export function pluralCategory(
  count: number,
  lang: string,
): "one" | "two" | "few" | "many" {
  const n = Math.abs(Math.trunc(count));

  if (n === 1) return "one";
  if (lang !== "ar") return "many";
  if (n === 2) return "two";
  if (n >= 3 && n <= 10) return "few";

  return "many";
}
